import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Zap } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api, money, pad, fmtDate, formatApiError } from "../lib/api";
import { PoolTracker } from "../components/PoolTracker";
import { VacationCard } from "../components/VacationCard";
import { VacationForm } from "../components/admin/VacationForm";
import { SettingsCard } from "../components/admin/SettingsCard";
import { OwnerPasswordCard } from "../components/admin/OwnerPasswordCard";
import { CharityManager } from "../components/admin/CharityManager";
import { AwardsList } from "../components/admin/AwardsList";
import { BrokerAdmin } from "../components/admin/BrokerAdmin";
import { ScaleDemo } from "../components/admin/ScaleDemo";
import { AuditLog } from "../components/admin/AuditLog";
import { LaunchChecklist } from "../components/admin/LaunchChecklist";
import { ReviewsAdmin } from "../components/admin/ReviewsAdmin";
import { ApprovalsAdmin, TeamAdmin } from "../components/admin/TeamAdmin";
import { TierSwitch } from "../components/TierSwitch";
import { useTier, TIER_META, PLAN_LABEL, partySize } from "../lib/tiers";

const Stat = ({ label, value, tid, gold }) => (
  <div className="lux-card p-6">
    <p className="eyebrow !text-slate-500">{label}</p>
    <p data-testid={tid} className={`font-mono-lux text-2xl md:text-3xl mt-3 ${gold ? "text-[#38BDF8]" : ""}`}>{value}</p>
  </div>
);

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [vacs, setVacs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [charities, setCharities] = useState([]);
  const [awards, setAwards] = useState([]);
  const [form, setForm] = useState({ open: false, item: null });
  const [tier, setTier] = useTier();
  const ts = stats?.tiers?.[tier];

  const loadMembers = useCallback(() => { api.get(`/admin/members?tier=${tier}`).then((r) => setMembers(r.data)); }, [tier]);
  useEffect(() => { loadMembers(); }, [loadMembers]);
  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    loadMembers();
    api.get("/vacations?include_inactive=true").then((r) => setVacs(r.data));
    api.get("/admin/payments").then((r) => setPayments(r.data));
    api.get("/charities?include_inactive=true").then((r) => setCharities(r.data));
    api.get("/admin/awards").then((r) => setAwards(r.data));
  }, [loadMembers]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => {
    try {
      const r = await fn();
      toast.success(typeof msg === "function" ? msg(r.data) : msg);
      (r.data?.offers || []).forEach((a) => toast(`Offer sent: ${a.user_name} → ${a.vacation_title}`));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveVac = (body) => act(() => (form.item ? api.put(`/admin/vacations/${form.item.id}`, body) : api.post("/admin/vacations", body)), "Vacation saved")
    .then(() => setForm({ open: false, item: null }));

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 pt-32 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Owner Control Center</p>
          <h1 className="font-serif-lux text-4xl sm:text-5xl mt-3">The Club Ledger</h1>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Your earnings" value={money(stats?.owner_earnings)} tid="admin-owner-earnings" gold />
        <Stat label="Revenue · founders" value={`${money(stats?.total_revenue)} · ${stats?.founders_claimed ?? 0}/${stats?.founder_limit ?? 500}`} tid="admin-total-revenue" />
        <Stat label={`${TIER_META[tier].short} · active / autopay`} value={`${ts?.active_members ?? 0} · ${ts?.autopay_members ?? 0}`} tid="admin-active-members" />
        <Stat label="Given to scholarships" value={money(stats?.charity_total)} tid="admin-charity-total" />
      </div>

      <TierSwitch value={tier} onChange={setTier} className="mt-8" />
      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8"><PoolTracker stats={ts} /></div>
        <div className="lg:col-span-4"><SettingsCard stats={stats} onSave={(b) => act(() => api.put("/admin/settings", b), "Settings updated")} /><OwnerPasswordCard /></div>
      </div>

      <Tabs defaultValue="members" className="mt-12">
        <TabsList className="bg-[#13192B] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="members" data-testid="admin-tab-members">Members</TabsTrigger>
          <TabsTrigger value="vacations" data-testid="admin-tab-vacations">Vacations</TabsTrigger>
          <TabsTrigger value="offers" data-testid="admin-tab-offers">Offers</TabsTrigger>
          <TabsTrigger value="charities" data-testid="admin-tab-charities">Schools</TabsTrigger>
          <TabsTrigger value="partners" data-testid="admin-tab-partners">Partners</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="scale" data-testid="admin-tab-scale">Scale demo</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="admin-tab-approvals">Approvals</TabsTrigger>
          <TabsTrigger value="team" data-testid="admin-tab-team">Team</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="launch" data-testid="admin-tab-launch">Launch</TabsTrigger>
        </TabsList>

        <TabsContent value="reviews" className="mt-6"><ReviewsAdmin /></TabsContent>
        <TabsContent value="approvals" className="mt-6"><ApprovalsAdmin /></TabsContent>
        <TabsContent value="team" className="mt-6"><TeamAdmin /></TabsContent>

        <TabsContent value="activity" className="mt-6"><AuditLog /></TabsContent>
        <TabsContent value="launch" className="mt-6"><LaunchChecklist /></TabsContent>

        <TabsContent value="scale" className="mt-6"><ScaleDemo /></TabsContent>

        <TabsContent value="members" className="mt-6">
          <div className="lux-card overflow-x-auto" data-testid="admin-members-table">
            <table className="w-full text-sm min-w-[980px]">
              <thead className="text-[10px] font-mono-lux tracking-[0.2em] uppercase text-slate-500 text-left">
                <tr className="border-b border-white/5"><th className="p-4">Pos</th><th>Member</th><th>Party</th><th>Status</th><th>Paid until</th><th>Autopay</th><th>Extra fund</th><th>Total paid</th><th></th></tr>
              </thead>
              <tbody>
                {members.filter((m) => m.tier === tier).map((m) => (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-[#283454]">
                    <td className="p-4 font-mono-lux text-[#38BDF8]">{pad(m.position)}</td>
                    <td><p>{m.user_name}{m.founder_number && <span className="ml-2 text-[10px] font-mono-lux text-[#38BDF8]">F#{m.founder_number}</span>}</p><p className="text-xs text-slate-500">{m.email}{m.referred_by && ` · ref ${m.referred_by}`}</p></td>
                    <td className="text-xs"><p className={m.level === "premium" ? "text-[#F43F5E] font-semibold" : "text-slate-400"}>{m.level}</p><p>{PLAN_LABEL[m.plan]}{m.extra_people ? ` +${m.extra_people}` : ""} ({partySize(m.plan, m.extra_people)})</p>
                      {m.companion_name && <p className="text-slate-500">{m.companion_name}</p>}{m.notes && <p className="text-amber-300/80">{m.notes}</p>}</td>
                    <td className="capitalize">{m.status}</td>
                    <td className="font-mono-lux text-slate-400">{fmtDate(m.paid_until)}</td>
                    <td>{m.autopay ? "On" : "—"}</td>
                    <td className="font-mono-lux text-[#38BDF8]">{money(m.extra_balance)}</td>
                    <td className="font-mono-lux">{money(m.total_paid)}</td>
                    <td className="pr-4 text-right">
                      {m.status === "active" && (
                        <button data-testid={`admin-lapse-btn-${m.id}`} onClick={() => act(() => api.post(`/admin/memberships/${m.id}/lapse`), `${m.user_name} removed (missed payment)`)}
                          className="text-xs text-red-300 hover:text-red-200">Mark missed payment</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="vacations" className="mt-6">
          <button data-testid="admin-add-vacation-btn" onClick={() => setForm({ open: true, item: null })} className="btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" />Add vacation</button>
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vacs.filter((v) => v.tier === tier).map((v) => (
              <VacationCard key={v.id} v={v} actions={<>
                <button data-testid={`admin-edit-vac-${v.id}`} onClick={() => setForm({ open: true, item: v })} className="btn-ghost px-4 py-2 text-xs">Edit</button>
                <button data-testid={`admin-delete-vac-${v.id}`} onClick={() => window.confirm("Delete this vacation?") && act(() => api.delete(`/admin/vacations/${v.id}`), "Vacation deleted")} className="btn-ghost px-4 py-2 text-xs text-red-300">Delete</button>
              </>} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="offers" className="mt-6"><AwardsList items={awards} /></TabsContent>

        <TabsContent value="partners" className="mt-6"><BrokerAdmin /></TabsContent>

        <TabsContent value="charities" className="mt-6">
          <CharityManager items={charities}
            onSave={(id, body) => act(() => (id ? api.put(`/admin/charities/${id}`, body) : api.post("/admin/charities", body)), "School saved")}
            onDelete={(id) => act(() => api.delete(`/admin/charities/${id}`), "School removed")} onReload={load} />
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <div className="lux-card divide-y divide-white/5" data-testid="admin-payments-list">
            {payments.map((p) => (
              <div key={p.id} className="grid grid-cols-2 md:grid-cols-5 gap-2 p-4 text-sm">
                <span>{p.user_name} <span className="text-xs text-slate-500">· {TIER_META[p.tier]?.short}</span></span>
                <span className="capitalize text-slate-400">{p.kind.replace("_", " ")}</span>
                <span className="font-mono-lux">{money(p.amount)}</span>
                <span className="font-mono-lux text-[#38BDF8]">+{money(p.owner_cut)} you</span>
                <span className="font-mono-lux text-slate-500">{fmtDate(p.created_at)}</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      <VacationForm open={form.open} onOpenChange={(o) => setForm({ ...form, open: o })} initial={form.item} onSave={saveVac} />
    </div>
  );
}
